import { useState } from 'react';
import styled from 'styled-components';
import { useSession } from '@/hooks';
import { useToast } from '@/ui';
import { Icon } from '@/icons';
import { media } from '@/theme/media';

const Bar = styled.div`
  position: fixed; z-index: 40; bottom: 24px;
  left: calc((100vw + var(--rail-w, 0px) - var(--right-w, 0px)) / 2);
  transform: translateX(-50%);
  width: min(540px, calc(100vw - var(--rail-w, 0px) - var(--right-w, 0px) - 40px));
  box-sizing: border-box;
  display: flex; align-items: center; gap: 12px;
  padding: 16px; border-radius: 14px;
  background: ${({ theme }) => theme.color.surfaceRaised};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  box-shadow: ${({ theme }) => theme.elevation.md};
  transition: left 260ms cubic-bezier(.22,.8,.25,1), width 260ms cubic-bezier(.22,.8,.25,1);
  ${media.mobile`
    left: 50%; width: calc(100vw - 24px);
    bottom: calc(72px + env(safe-area-inset-bottom));
    padding: 12px; gap: 8px; flex-wrap: wrap;
  `}
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;
const Text = styled.div`
  flex: 1; min-width: 0; font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  line-height: 1.45;
  strong { font-weight: 600; }
  p { margin: 4px 0 0; font-size: ${({ theme }) => theme.fontSize.xs}; color: ${({ theme }) => theme.color.textMuted}; }
  ${media.mobile`flex-basis: calc(100% - 56px); order: 1;`}
`;
const ResendBtn = styled.button`
  font: inherit; font-weight: 600; font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textPrimary}; background: transparent;
  border: 1px solid ${({ theme }) => theme.color.hairSoft}; border-radius: ${({ theme }) => theme.radius.md};
  min-height: 44px; padding: 8px 12px; cursor: pointer;
  &:hover { background: ${({ theme }) => theme.color.hover}; }
  &:disabled { opacity: 0.6; cursor: default; }
  ${media.mobile`order: 3;`}
`;
const Dismiss = styled.button`
  background: transparent; border: none; cursor: pointer; padding: 0; line-height: 0;
  display: grid; place-items: center; width: 44px; height: 44px; flex: 0 0 44px;
  border-radius: ${({ theme }) => theme.radius.sm}; align-self: flex-start;
  color: ${({ theme }) => theme.color.textMuted};
  &:hover { background: ${({ theme }) => theme.color.hover}; }
  ${media.mobile`order: 2;`}
`;

const dismissalKey = (userId: string) => `truxsocial:verification-dismissed:${userId}`;

function wasDismissed(userId: string): boolean {
  try { return sessionStorage.getItem(dismissalKey(userId)) === 'yes'; }
  catch { return false; }
}

function VerificationNotice({ userId, resendVerification }: {
  userId: string;
  resendVerification: () => Promise<void>;
}) {
  const toast = useToast();
  const [dismissed, setDismissed] = useState(() => wasDismissed(userId));
  const [busy, setBusy] = useState(false);

  if (dismissed) return null;

  function dismiss() {
    setDismissed(true);
    try { sessionStorage.setItem(dismissalKey(userId), 'yes'); }
    catch { /* In-memory dismissal still works when storage is unavailable. */ }
  }

  async function handleResend() {
    setBusy(true);
    try {
      await resendVerification();
      toast('Verification email sent. Check your inbox');
    } catch (e) {
      toast(
        e instanceof Error && e.name === 'RateLimited'
          ? 'Please wait a moment before requesting another email'
          : 'Could not resend right now. Please try again',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Bar role="status" aria-label="Email verification reminder" data-testid="verification-notice">
      <Text>
        <strong>Verify your email to post and interact.</strong>
        <p>We sent a link to your inbox.</p>
      </Text>
      <ResendBtn type="button" onClick={() => void handleResend()} disabled={busy}>
        {busy ? 'Sending…' : 'Resend'}
      </ResendBtn>
      <Dismiss type="button" aria-label="Dismiss email verification reminder" onClick={dismiss}>
        <Icon name="close" size={16} />
      </Dismiss>
    </Bar>
  );
}

export function VerifyEmailBanner() {
  const { currentUser, isAuthenticated, isEmailVerified, resendVerification } = useSession();
  if (!isAuthenticated || isEmailVerified || !currentUser) return null;
  // Key by account: dismissing one member's reminder must not hide another's.
  return <VerificationNotice key={currentUser.id} userId={currentUser.id} resendVerification={resendVerification} />;
}
